import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";
import { DevelopmentUpdate } from "../../../store/types/development-updates-types";
import DevUpdateImageGallery from "./DevUpdateImageGallery";

interface Props {
  update: DevelopmentUpdate;
  isFirst: boolean;
}

const DevelopmentUpdateItem: React.FC<Props> = ({ update, isFirst }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const MAX_HEIGHT = 400; // Maximum height when collapsed (should match gallery height)

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isBlocksContent = typeof update.description !== "string";

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setShowReadMore(contentHeight > MAX_HEIGHT);
    }
  }, [update.description]);

  return (
    <motion.article
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
      }}
      transition={{ duration: 0.6 }}
      className={`mb-[6rem] ${isFirst ? "mt-0" : "mt-[4rem]"}`}
    >
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left side - Description */}
        <div className="flex-1 lg:pr-8 order-2 lg:order-1 flex flex-col">
          <div className="bg-white rounded-lg p-6 md:p-8 shadow-md flex-1 flex flex-col h-full">
            <div className="mb-6">
              <h3 className="text-black mb-[2.5rem] text-[3rem] font-bold">
                {update.title}
              </h3>
              {update.updateDate && (
                <p className="text-gray-600">
                  Updated: {formatDate(update.updateDate)}
                </p>
              )}
            </div>

            <div
              ref={contentRef}
              className={`text-black prose prose-lg max-w-none flex-1 overflow-hidden transition-all duration-300 relative ${
                !isExpanded ? "max-h-[400px]" : ""
              }`}
              style={{
                maxHeight: isExpanded ? "none" : `${MAX_HEIGHT}px`,
              }}
            >
              <div className={`${!isExpanded ? "overflow-hidden" : ""}`}>
                {isBlocksContent ? (
                  <BlocksRenderer
                    blocks={{
                      paragraph: ({ children }) => (
                        <p className="mb-[2.5rem] leading-relaxed text-black">
                          {children}
                        </p>
                      ),
                      image: ({ children, image }) => (
                        <img
                          className="mb-4 w-full mx-auto rounded-lg"
                          src={image.url}
                          alt={image.name}
                        />
                      ),
                      heading: ({ children, level }) => {
                        const HeadingTag =
                          `h${level}` as keyof JSX.IntrinsicElements;
                        const sizeClasses = {
                          1: "text-3xl md:text-4xl",
                          2: "text-2xl md:text-3xl",
                          3: "text-xl md:text-2xl",
                        };
                        return (
                          <HeadingTag
                            className={`mb-2 mt-4 text-black font-semibold ${
                              sizeClasses[level as keyof typeof sizeClasses] ||
                              "text-xl"
                            }`}
                          >
                            {children}
                          </HeadingTag>
                        );
                      },
                    }}
                    content={update.description as BlocksContent}
                  />
                ) : (
                  <p className="mb-4 font-light leading-relaxed whitespace-pre-line text-black text-base md:text-lg">
                    {String(update.description)}
                  </p>
                )}
              </div>
              {!isExpanded && showReadMore && (
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" />
              )}
            </div>

            {showReadMore && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="bg-primary flex items-center border border-transparent text-black px-[1.2rem] py-[0.4rem] rounded-[0.5rem] h-[5rem] hover:bg-yellow-400"
                >
                  <p className="whitespace-nowrap p-[1rem]">
                    {isExpanded ? "Read less" : "Read more"}
                  </p>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right side - Gallery */}
        <div className="flex-1 lg:max-w-[50%] order-1 lg:order-2 h-full">
          {update.videoLink ||
          (update.devUpdateImages && update.devUpdateImages.length > 0) ? (
            <DevUpdateImageGallery
              images={update.devUpdateImages || []}
              videoLink={update.videoLink}
            />
          ) : (
            <div
              className="bg-gray-100 rounded-lg p-8 text-center text-gray-500 h-full flex items-center justify-center"
              style={{ minHeight: "400px" }}
            >
              No media available for this update
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
};

export default DevelopmentUpdateItem;
